import type {ReactElement} from 'react';
import type {SocialPlatform} from '@Components/social-media/social-media.types';
import {SocialUniquePlatforms} from '@Components/social-media/social-media.types';
import {SocialAccountButton} from '@Components/social-media/components/social-account-button';
import {SocialPlatformConnectionDropdown} from './components/social-platform-connection-dropdown/social-platform-connection-dropdown';
import type {SocialPlatformConnectGridProps} from './social-platform-connect-grid.types';
import {CENTERED_DROPDOWN_CARD_ROW_INDEX, FIRST_UPWARD_OPENING_CARD_ROW_INDEX, PLATFORM_GRID_COLUMNS} from './social-platform-connect-grid.types';
import styles from './social-platform-connect-grid.module.scss';

export function SocialPlatformConnectGrid({activePlatform, isMobileLayout, onPlatformCardClick, onCancelConnection, onAccountConnected}: SocialPlatformConnectGridProps): ReactElement {
  const getConnectionDropdown = (platform: SocialPlatform): ReactElement => {
    return (
      <SocialPlatformConnectionDropdown
        key={`${platform}-connection`}
        platform={platform}
        hasFullWidthActions={isMobileLayout}
        onCancel={onCancelConnection}
        onAccountConnected={onAccountConnected}
      />
    );
  };

  const getPlatformCard = (platform: SocialPlatform): ReactElement => {
    return (
      <SocialAccountButton
        key={platform}
        type={platform}
        isSelected={platform === activePlatform}
        onClick={(): void => {
          onPlatformCardClick(platform);
        }}
      />
    );
  };

  const getFloatingConnectionDropdown = (platformsInRow: SocialPlatform[], cardRowIndex: number): ReactElement | null => {
    if (activePlatform === null || !platformsInRow.includes(activePlatform)) {
      return null;
    }

    const isActivePlatformInLastColumn = platformsInRow.indexOf(activePlatform) === PLATFORM_GRID_COLUMNS - 1;
    const isCenteredOnRow = cardRowIndex === CENTERED_DROPDOWN_CARD_ROW_INDEX;

    const getAlignmentClass = (): string => {
      if (isCenteredOnRow) {
        return isActivePlatformInLastColumn ? styles.connectionDropdownOverlayCenteredEndColumn : styles.connectionDropdownOverlayCenteredStartColumn;
      }

      return isActivePlatformInLastColumn ? styles.connectionDropdownOverlayEndAligned : styles.connectionDropdownOverlayStartAligned;
    };

    const getOpeningDirectionClass = (): string => {
      if (isCenteredOnRow) {
        return styles.connectionDropdownOverlayCenteredOnRow;
      }

      if (cardRowIndex >= FIRST_UPWARD_OPENING_CARD_ROW_INDEX) {
        return styles.connectionDropdownOverlayOpensUpward;
      }

      return '';
    };

    const alignmentClass = getAlignmentClass();
    const openingDirectionClass = getOpeningDirectionClass();

    return <div className={`${styles.connectionDropdownOverlay} ${alignmentClass} ${openingDirectionClass}`}>{getConnectionDropdown(activePlatform)}</div>;
  };

  const getPlatformCardRows = (): ReactElement[] => {
    const cardRows: ReactElement[] = [];

    for (let rowStartIndex = 0; rowStartIndex < SocialUniquePlatforms.length; rowStartIndex += PLATFORM_GRID_COLUMNS) {
      const platformsInRow = SocialUniquePlatforms.slice(rowStartIndex, rowStartIndex + PLATFORM_GRID_COLUMNS);

      cardRows.push(
        <div key={platformsInRow.join('-')} className={styles.platformCardRow}>
          {platformsInRow.map(getPlatformCard)}
          {getFloatingConnectionDropdown(platformsInRow, rowStartIndex / PLATFORM_GRID_COLUMNS)}
        </div>
      );
    }

    return cardRows;
  };

  const getInlineConnectionDropdown = (platform: SocialPlatform): ReactElement | null => {
    if (platform !== activePlatform) {
      return null;
    }

    return getConnectionDropdown(platform);
  };

  const getPlatformCardList = (): ReactElement[] => {
    const listItems: ReactElement[] = [];

    SocialUniquePlatforms.forEach((platform) => {
      listItems.push(getPlatformCard(platform));
      const inlineDropdown = getInlineConnectionDropdown(platform);

      if (inlineDropdown !== null) {
        listItems.push(inlineDropdown);
      }
    });

    return listItems;
  };

  const getGridContent = (): ReactElement[] => {
    return isMobileLayout ? getPlatformCardList() : getPlatformCardRows();
  };

  return <>{getGridContent()}</>;
}
